import { Column, RenderIf } from 'react-rainbow-components';
import { DateTime } from 'luxon';
import Table from 'components/Table';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import ChevronRight from 'components/icons/chevronRight';
import ChevronLeft from 'components/icons/chevronLeft';
import DateTimeColumn from 'components/DateTimeColumn';
import PageCard from 'components/PageComponents/card';
import PageHeader from 'components/PageComponents/header';
import HeaderText from 'components/PageComponents/headerText';
import HeaderTextDescription from 'components/PageComponents/headerTextDescription';
import useHttpMutation from 'data/firestore/useHttpMutation';
import useReminders from 'data/firestore/agent/reminder/useCollectionWithPagination';
import { useParams } from 'react-router-dom';
import {
    query,
    where,
    orderBy,
    QueryConstraint,
} from 'firebase/firestore';
import useSearchFilters from 'hooks/useSearchFilters';
import { useConnectModal, useOpenModal } from '@rainbow-modules/hooks';
import { Reminder } from 'data/firestore/agent/reminder/types';
import { useCallback } from 'react';
import {
    hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { PAGE_SIZE } from '../../constants';
import StatusColumn from './columns/status';
import PurposeColumn from './columns/purpose';
import {
    Container, TableContainer,
    StyledDrawer,
    PaginationContainer,
    EntriesText,
    PaginationButtons,
    DrawerFooter,
} from './styled';
import FilterButton from './FilterButton';
import ReminderDetails from './ReminderDetails';
import CancelModal from './CancelModal';
import useConfirmModal from './hooks/useConfirmModal';

const CANCEL_REMINDER_CONFIRM_MODAL = 'confirm-cancel-reminder';

const RemindersPage = () => {
    const { agentId } = useParams();
    const {
        values,
    } = useSearchFilters({ filterNames: ['status', 'timestamp'] });
    const { reminder = {}, ...connectedReminderModalProps } = useConnectModal(
        'reminder-modal',
    );
    const { confirm: confirmModal } = useConfirmModal(CANCEL_REMINDER_CONFIRM_MODAL);
    const [openDrawer, closeDrawer] = useOpenModal('reminder-modal');

    const { mutateAsync: cancelReminder } = useHttpMutation({
        method: 'DELETE',
        onSuccess: () => {
            closeDrawer();
        },
    });

    const {
        data: reminders, isLoading, totalRecords, nextPage, prevPage, firstPage, hasMore,
    } = useReminders(agentId as string, {
        limit: PAGE_SIZE,
        listQueryFn: (ref) => {
            const filters: QueryConstraint[] = [];
            if (typeof values.status === 'string' && values.status.length > 0) {
                filters.push(where('status', 'in', values.status.split(',')));
            }
            if (typeof values.timestamp === 'string' && values.timestamp.length > 0) {
                const [dateFrom, dateTo] = values.timestamp.split(',');
                const from = DateTime.fromISO(dateFrom).toMillis();
                const to = DateTime.fromISO(dateTo).toMillis();
                filters.push(
                    where('timestamp', '>=', from),
                    where('timestamp', '<=', to),
                );
            }
            return query(ref, ...filters, orderBy('timestamp', 'desc'));
        },
        track: [String(values.status), String(values.timestamp)],
    });

    const handleCancelReminder = useCallback(async () => {
        const { id: reminderId } = reminder as Reminder;
        if (!reminderId) return;
        const result = await confirmModal({
            variant: 'destructive',
            borderRadius: 'semi-square',
            okButtonLabel: 'Cancel',
            cancelButtonLabel: 'Keep',
        });

        if (!result.confirm) {
            return;
        }

        showAppSpinner();
        try {
            await cancelReminder({
                pathname: `/agents/${agentId}/reminders/${reminderId}`,
                body: {
                    reason: result.reason || null,
                },
            });
            showAppNotification({
                icon: 'success',
                title: 'Reminder Canceled!',
                description: 'Reminder was canceled.',
                timeout: 5000,
            });
        } catch (error) {
            const message = (error as any)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }

        hideAppSpinner();
    }, [agentId, cancelReminder, confirmModal, reminder]);

    const canCancel = !['completed', 'cancelled'].includes((reminder as Reminder)?.status);

    return (
        <PageCard>
            <Container>
                <PageHeader>
                    <div>
                        <HeaderText>Reminders</HeaderText>
                        <HeaderTextDescription>
                            View and track upcoming scheduled events and follow-ups
                        </HeaderTextDescription>
                    </div>
                    <FilterButton label="Filters" />
                </PageHeader>
                <TableContainer>
                    <Table
                        isLoading={isLoading}
                        keyField="id"
                        data={reminders}
                    >
                        <Column
                            header="Purpose"
                            field="purpose"
                            component={PurposeColumn}
                            onClick={((value: Reminder) => openDrawer({
                                reminder: value,
                            }))}
                        />
                        <Column
                            header="Created At"
                            field="createdAt"
                            component={DateTimeColumn}
                            defaultWidth={210}
                            showRelativeDate
                        />
                        <Column
                            header="Scheduled At"
                            field="timestamp"
                            component={DateTimeColumn}
                            defaultWidth={210}
                            showRelativeDate
                        />
                        <Column
                            header="Status"
                            field="status"
                            component={StatusColumn}
                            defaultWidth={150}
                        />
                    </Table>
                    <PaginationContainer>
                        <EntriesText>
                            {`Total:  ${totalRecords} entries`}
                        </EntriesText>
                        <PaginationButtons>
                            <ButtonIcon
                                title="previous"
                                icon={<ChevronLeft />}
                                borderRadius="semi-square"
                                onClick={prevPage}
                                size="small"
                                disabled={isLoading || firstPage}
                                variant="border-filled"
                            />
                            <ButtonIcon
                                title="next"
                                icon={<ChevronRight />}
                                size="small"
                                borderRadius="semi-square"
                                onClick={nextPage}
                                disabled={isLoading || !hasMore}
                                variant="border-filled"
                            />
                        </PaginationButtons>
                    </PaginationContainer>
                </TableContainer>
            </Container>
            <StyledDrawer
                {...connectedReminderModalProps}
                slideFrom="right"
                header="Reminder Details"
                footer={(
                    <DrawerFooter>
                        <Button label="Close" borderRadius="semi-rounded" variant="neutral" onClick={closeDrawer} />
                        <RenderIf isTrue={canCancel}>
                            <Button label="Cancel reminder" borderRadius="semi-rounded" variant="brand" onClick={handleCancelReminder} />
                        </RenderIf>
                    </DrawerFooter>
                )}
            >
                <ReminderDetails reminder={reminder as Reminder} />
            </StyledDrawer>
            <CancelModal cancelationReasonRequired id={CANCEL_REMINDER_CONFIRM_MODAL} />
        </PageCard>
    );
};

export default RemindersPage;
